/**
 * Interceptor for HTTP requests, used to handle authentication errors
 in regular, non-Apollo/GraphQL requests
 */

let sessionExpiredHandler: () => void;

/**
 * Registers a function to be called when an authentication error occurs.
 * The function will be called when an HTTP request (not an Apollo/GraphQL request)
 * receives a 401 or 403 status code, indicating an authentication error.
 * If called, the function should handle the error, e.g. by logging out the user.
 * @param handler - The function to be called when an authentication error occurs.
 */
export const registerSessionExpiredHandler = (handler: () => void) => {
  sessionExpiredHandler = handler;
};

const ALLOWED_ORIGINS = [window.location.origin];

const parseAndValidateUrl = (url: string): URL => {
  const parsed = new URL(url, window.location.origin);
  if (!ALLOWED_ORIGINS.includes(parsed.origin)) {
    throw new Error(`Request to disallowed origin: ${parsed.origin}`);
  }
  return parsed;
};

// Function to make fetch requests with authentication error handling
export const fetchWithAuthHandling = async (url: string, options?: RequestInit) => {
  const safeUrl = parseAndValidateUrl(url);
  try {
    const response = await fetch(safeUrl, options);

    // Auth HTTP error management
    if (response.status === 401 || response.status === 403) {
      if (sessionExpiredHandler) {
        sessionExpiredHandler();
      }
      throw new Error('Your session has expired. Please log in again.');
    }

    return response;
  } catch (error) {
    // If is a network error, check the token
    if (error instanceof Error && error.message.includes('Failed to fetch')) {
      const idToken = sessionStorage.getItem('idToken');
      const refreshToken = sessionStorage.getItem('refreshToken');
      if ((!idToken && sessionExpiredHandler) || !refreshToken) {
        sessionExpiredHandler();
      }
    }
    throw error;
  }
};

/**
 * Sets up an interceptor for HTTP requests to handle authentication errors.
 * If the HTTP request (not an Apollo/GraphQL request) receives a 401 or 403 status code,
 * the handler registered with registerSessionExpiredHandler will be called.
 * If the request fails due to a network error, the handler will also be called
 * if there is no idToken in the session storage.
 */
/* export const setupHttpInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const response = await originalFetch(input, init);

      // Check if it's an auth error
      if (response.status === 401 || response.status === 403) {
        if (sessionExpiredHandler) {
          sessionExpiredHandler();
        }
        // Add a promise rejection to ensure the error is propagated
        return Promise.reject(
          new Error('Your session has expired. Please log in again.')
        );
      }

      return response;
    } catch (error) {
      // If is a network error, check the token
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        const idToken = sessionStorage.getItem('idToken');
        const refreshToken = sessionStorage.getItem('refreshToken');
        if ((!idToken && sessionExpiredHandler) || !refreshToken) {
          sessionExpiredHandler();
        }
      }
      throw error;
    }
  };
}; */

export const triggerSessionExpiredHandler = () => {
  if (sessionExpiredHandler) sessionExpiredHandler();
};
